import { request, API_BASE } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';

// ================================
// NEW ANALYTICS & DASHBOARDS
// ================================

// Equipment & Maintenance (from production route)
export async function getProductionEquipment(params?: { status?: string; type?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/equipment?${query}`);
}

export async function createProductionEquipment(data: any) {
  return request<{ data: any }>('/equipment', { method: 'POST', body: JSON.stringify(data) });
}

export async function getProductionWorkOrders(params?: { status?: string; priority?: string; type?: string; equipmentId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.type) query.set('type', params.type);
  if (params?.equipmentId) query.set('equipmentId', params.equipmentId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/maintenance/work-orders?${query}`);
}

export async function createProductionWorkOrder(data: any) {
  return request<{ data: any }>('/maintenance/work-orders', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateWorkOrderStatus(id: string, data: { status: string; notes?: string; actualHours?: number }) {
  return request<{ data: any }>(`/maintenance/work-orders/${id}/status`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function getMaintenanceDashboard() {
  return request<{ data: any }>('/maintenance/dashboard');
}

export async function getMaintenanceDowntimeReport(params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return request<{ data: any }>(`/maintenance/downtime-report?${query}`);
}

export async function getProductionOverdueMaintenance() {
  return request<{ data: any }>('/maintenance/overdue');
}

export async function recordEquipmentFailure(equipmentId: string, data: any) {
  return request<{ data: any }>(`/equipment/${equipmentId}/failures`, { method: 'POST', body: JSON.stringify(data) });
}

export async function getEquipmentFailures(equipmentId: string) {
  return request<{ data: any[] }>(`/equipment/${equipmentId}/failures`);
}

// Quality CAPA Management (from quality route)
export async function getQualityCapaList(params?: { status?: string; type?: string; riskLevel?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.riskLevel) query.set('riskLevel', params.riskLevel);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/quality/capa?${query}`);
}

export async function getQualityCapa(id: string) {
  return request<{ data: any }>(`/quality/capa/${id}`);
}

export async function createQualityCapa(data: any) {
  return request<{ data: any }>('/quality/capa', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateQualityCapaStatus(id: string, data: any) {
  return request<{ data: any }>(`/quality/capa/${id}/status`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function addQualityCapaAction(capaId: string, data: any) {
  return request<{ data: any }>(`/quality/capa/${capaId}/actions`, { method: 'POST', body: JSON.stringify(data) });
}

export async function updateQualityCapaActionStatus(capaId: string, actionId: string, data: any) {
  return request<{ data: any }>(`/quality/capa/${capaId}/actions/${actionId}/status`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function getQualityCapaDashboard() {
  return request<{ data: any }>('/quality/capa/dashboard');
}

// Customer Credit Management (from crm route)
export async function getCustomerCredit(customerId: string) {
  return request<{ data: any }>(`/crm/customers/${customerId}/credit`);
}

export async function updateCustomerCreditLimit(customerId: string, data: { creditLimit: number; reason?: string }) {
  return request<{ data: any }>(`/crm/customers/${customerId}/credit-limit`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function placeCustomerCreditHold(customerId: string, data: { reason: string }) {
  return request<{ data: any }>(`/crm/customers/${customerId}/credit-hold`, { method: 'POST', body: JSON.stringify(data) });
}

export async function releaseCustomerCreditHold(customerId: string, data?: { reason?: string }) {
  return request<{ data: any }>(`/crm/customers/${customerId}/credit-release`, { method: 'POST', body: JSON.stringify(data || {}) });
}

export async function getCustomerCreditHistory(customerId: string) {
  return request<{ data: any[] }>(`/crm/customers/${customerId}/credit-history`);
}

export async function getCreditDashboard() {
  return request<{ data: any }>('/crm/credit/dashboard');
}

// Procurement Spend Analysis (from purchase-orders route)
export async function getSpendBySupplier(params?: { startDate?: string; endDate?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  return request<{ data: any }>(`/purchase-orders/analytics/spend-by-supplier?${query}`);
}

export async function getSpendByMaterial(params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return request<{ data: any }>(`/purchase-orders/analytics/spend-by-material?${query}`);
}

export async function getSpendTrend(months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/purchase-orders/analytics/spend-trend${query}`);
}

export async function getProcurementDashboard() {
  return request<{ data: any }>('/purchase-orders/analytics/dashboard');
}

// Sales Analytics (from sales route)
export async function getSalesByCustomer(params?: { startDate?: string; endDate?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  return request<{ data: any }>(`/sales/analytics/by-customer?${query}`);
}

export async function getSalesByProduct(params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return request<{ data: any }>(`/sales/analytics/by-product?${query}`);
}

export async function getSalesTrend(months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/sales/analytics/trend${query}`);
}

export async function getQuoteConversion(months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/sales/analytics/quote-conversion${query}`);
}

export async function getSalesDashboard() {
  return request<{ data: any }>('/sales/analytics/dashboard');
}

// Inventory Valuation & Analytics (from inventory route)
export async function getInventoryValuation(params?: { warehouseId?: string; category?: string }) {
  const query = new URLSearchParams();
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.category) query.set('category', params.category);
  return request<{ data: any }>(`/inventory/valuation?${query}`);
}

export async function getExpiringInventory(days?: number) {
  const query = days ? `?days=${days}` : '';
  return request<{ data: any }>(`/inventory/expiring${query}`);
}

export async function getMovementAnalysis(params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return request<{ data: any }>(`/inventory/movement-analysis?${query}`);
}

export async function getInventoryDashboard() {
  return request<{ data: any }>('/inventory/dashboard');
}

// Shipping Analytics (from shipping route)
export async function getCarrierPerformance(params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return request<{ data: any }>(`/shipping/analytics/carrier-performance?${query}`);
}

export async function getDeliveryTimes(months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/shipping/analytics/delivery-times${query}`);
}

export async function getShippingByDestination(params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return request<{ data: any }>(`/shipping/analytics/by-destination?${query}`);
}

export async function getShippingDashboard() {
  return request<{ data: any }>('/shipping/analytics/dashboard');
}

// Raw Materials Price Analysis (from raw-materials route)
export async function getMaterialPriceHistory(id: string, months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/raw-materials/${id}/price-history${query}`);
}

export async function getMaterialVendorComparison(id: string) {
  return request<{ data: any }>(`/raw-materials/${id}/vendor-comparison`);
}

export async function getPriceOverview() {
  return request<{ data: any }>('/raw-materials/analytics/price-overview');
}

// Batch Records Analytics (from batch-records route)
export async function getBatchCompletionAnalytics(months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/batch-records/analytics/completion${query}`);
}

export async function getBatchDeviationAnalytics(months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/batch-records/analytics/deviations${query}`);
}

export async function getBatchTemplateAnalytics() {
  return request<{ data: any }>('/batch-records/analytics/templates');
}

export async function getBatchRecordsDashboard() {
  return request<{ data: any }>('/batch-records/analytics/dashboard');
}

// ==========================================
// SHOPIFY INTEGRATION
// ==========================================

// Store Management
export async function getShopifyStores(params?: { status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/shopify/stores?${query}`);
}

export async function getShopifyStore(id: string) {
  return request<{ data: any }>(`/shopify/stores/${id}`);
}

export async function createShopifyStore(data: { shopDomain: string; accessToken: string; storeName?: string; settings?: any }) {
  return request<{ data: any }>('/shopify/stores', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateShopifyStore(id: string, data: { storeName?: string; settings?: any; isActive?: boolean }) {
  return request<{ data: any }>(`/shopify/stores/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteShopifyStore(id: string) {
  return request<{ success: boolean }>(`/shopify/stores/${id}`, { method: 'DELETE' });
}

export async function testShopifyConnection(id: string) {
  return request<{ success: boolean; message?: string }>(`/shopify/stores/${id}/test`, { method: 'POST' });
}

// Product Sync
export async function getShopifyProducts(params?: { status?: string; search?: string; storeId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/shopify/products?${query}`);
}

export async function getShopifyProduct(id: string) {
  return request<{ data: any }>(`/shopify/products/${id}`);
}

export async function syncAllShopifyProducts(storeId?: string) {
  const body = storeId ? { storeId } : {};
  return request<{ success: boolean; jobId?: string }>('/shopify/products/sync', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function syncShopifyProduct(sourceId: string) {
  return request<{ success: boolean }>(`/shopify/products/sync/${sourceId}`, { method: 'POST' });
}

// Inventory Sync
export async function syncShopifyInventory(storeId?: string) {
  const body = storeId ? { storeId } : {};
  return request<{ success: boolean; jobId?: string }>('/shopify/inventory/sync', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getShopifyInventoryLevels(variantId: string) {
  return request<{ data: any[] }>(`/shopify/inventory/levels?variantId=${variantId}`);
}

// Order Sync
export async function getShopifyOrders(params?: { status?: string; financialStatus?: string; fulfillmentStatus?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.financialStatus) query.set('financialStatus', params.financialStatus);
  if (params?.fulfillmentStatus) query.set('fulfillmentStatus', params.fulfillmentStatus);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/shopify/orders?${query}`);
}

export async function getShopifyOrder(id: string) {
  return request<{ data: any }>(`/shopify/orders/${id}`);
}

export async function processShopifyOrder(id: string) {
  return request<{ success: boolean; fromaOrderId?: string }>(`/shopify/orders/${id}/process`, { method: 'POST' });
}

export async function fulfillShopifyOrder(id: string, data: { trackingNumber?: string; carrier?: string; lineItems?: any[] }) {
  return request<{ success: boolean }>(`/shopify/orders/${id}/fulfill`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Webhooks
export async function registerShopifyWebhooks(storeId: string) {
  return request<{ success: boolean; webhooks?: any[] }>('/shopify/webhooks/register', {
    method: 'POST',
    body: JSON.stringify({ storeId }),
  });
}

export async function getShopifyWebhooks(storeId?: string) {
  const query = storeId ? `?storeId=${storeId}` : '';
  return request<{ data: any[] }>(`/shopify/webhooks${query}`);
}

// Sync Jobs
export async function getShopifySyncJobs(params?: { storeId?: string; jobType?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.jobType) query.set('jobType', params.jobType);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/shopify/sync-jobs?${query}`);
}

export async function getShopifySyncJob(id: string) {
  return request<{ data: any }>(`/shopify/sync-jobs/${id}`);
}

// Webhook Events
export async function getShopifyWebhookEvents(params?: { storeId?: string; topic?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.topic) query.set('topic', params.topic);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/shopify/webhook-events?${query}`);
}

export async function retryShopifyWebhookEvent(id: string) {
  return request<{ success: boolean }>(`/shopify/webhook-events/${id}/retry`, { method: 'POST' });
}

// Dashboard
export async function getShopifyDashboardStats() {
  return request<{ data: any }>('/shopify/dashboard/stats');
}

// ==========================================
// ECOMMERCE MANAGEMENT
// ==========================================

// Stores
export async function getEcommerceStores(params?: { organizationId?: string; status?: string; search?: string; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.organizationId) query.set('organizationId', params.organizationId);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/stores?${query}`);
}

export async function createEcommerceStore(data: { organizationId: string; storeName: string; storeUrl?: string; platform?: string; currency?: string; settings?: any }) {
  return request<{ data: any }>('/ecommerce/stores', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEcommerceStore(id: string, data: { storeName?: string; storeUrl?: string; status?: string; settings?: any }) {
  return request<{ data: any }>(`/ecommerce/stores/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Store Locations
export async function getEcommerceStoreLocations(storeId?: string) {
  const query = storeId ? `?storeId=${storeId}` : '';
  return request<{ data: any[] }>(`/ecommerce/store-locations${query}`);
}

export async function createEcommerceStoreLocation(data: { storeId: string; locationName: string; addressLine1?: string; city?: string; country?: string; isDefault?: boolean }) {
  return request<{ data: any }>('/ecommerce/store-locations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Products
export async function getEcommerceProducts(params?: { storeId?: string; status?: string; search?: string; category?: string; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.category) query.set('category', params.category);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/products?${query}`);
}

export async function createEcommerceProduct(data: { storeId: string; productName: string; sku?: string; description?: string; price?: number; status?: string; category?: string; tags?: string[]; images?: string[] }) {
  return request<{ data: any }>('/ecommerce/products', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEcommerceProduct(id: string, data: { productName?: string; sku?: string; description?: string; price?: number; status?: string; category?: string }) {
  return request<{ data: any }>(`/ecommerce/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Carts
export async function getEcommerceCarts(params?: { storeId?: string; customerId?: string; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/carts?${query}`);
}

export async function createEcommerceCart(data: { storeId: string; customerId?: string; sessionId?: string; items?: any[] }) {
  return request<{ data: any }>('/ecommerce/carts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEcommerceCart(id: string, data: { items?: any[]; subtotal?: number; total?: number }) {
  return request<{ data: any }>(`/ecommerce/carts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Orders
export async function getEcommerceOrders(params?: { storeId?: string; customerId?: string; status?: string; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/orders?${query}`);
}

export async function createEcommerceOrder(data: { storeId: string; customerId?: string; items: any[]; shippingAddress?: any; billingAddress?: any }) {
  return request<{ data: any }>('/ecommerce/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEcommerceOrder(id: string, data: { status?: string; paymentStatus?: string; fulfillmentStatus?: string }) {
  return request<{ data: any }>(`/ecommerce/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Customers
export async function getEcommerceCustomers(params?: { storeId?: string; search?: string; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/customers?${query}`);
}

export async function createEcommerceCustomer(data: { storeId: string; email: string; firstName?: string; lastName?: string; phone?: string; acceptsMarketing?: boolean }) {
  return request<{ data: any }>('/ecommerce/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEcommerceCustomer(id: string, data: { firstName?: string; lastName?: string; phone?: string; acceptsMarketing?: boolean; tags?: string[] }) {
  return request<{ data: any }>(`/ecommerce/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Coupons
export async function getEcommerceCoupons(params?: { storeId?: string; isActive?: boolean; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/coupons?${query}`);
}

export async function createEcommerceCoupon(data: { storeId: string; code: string; description?: string; discountType?: string; discountValue?: number; minimumPurchase?: number; usageLimit?: number; startDate?: string; endDate?: string }) {
  return request<{ data: any }>('/ecommerce/coupons', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Reviews
export async function getEcommerceReviews(params?: { storeId?: string; productId?: string; status?: string; rating?: string; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.productId) query.set('productId', params.productId);
  if (params?.status) query.set('status', params.status);
  if (params?.rating) query.set('rating', params.rating);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/reviews?${query}`);
}

export async function createEcommerceReview(data: { storeId: string; productId: string; customerId?: string; rating: number; title?: string; content?: string }) {
  return request<{ data: any }>('/ecommerce/reviews', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Wishlists
export async function getEcommerceWishlists(params?: { customerId?: string; storeId?: string; page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  return request<{ data: any[]; total: number }>(`/ecommerce/wishlists?${query}`);
}

export async function addToEcommerceWishlist(data: { customerId: string; productId: string; storeId: string }) {
  return request<{ data: any }>('/ecommerce/wishlists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Metrics
export async function getEcommerceMetrics(params?: { organizationId?: string; storeId?: string; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.organizationId) query.set('organizationId', params.organizationId);
  if (params?.storeId) query.set('storeId', params.storeId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return request<{ data: any }>(`/ecommerce/metrics?${query}`);
}

export async function createEcommerceMetrics(data: { organizationId: string; storeId?: string; periodStart: string; periodEnd: string; totalOrders?: number; totalRevenue?: number; averageOrderValue?: number }) {
  return request<{ data: any }>('/ecommerce/metrics', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ==========================================
// BATCH RECORDS (Manufacturing)
// ==========================================

// Templates
export async function getBatchRecordTemplates(params?: { formulaId?: string; status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/batch-records/templates?${query}`);
}

export async function getBatchRecordTemplate(id: string) {
  return request<{ data: any }>(`/batch-records/templates/${id}`);
}

export async function createBatchRecordTemplate(data: { formulaId: string; templateName: string; version?: string; steps?: any[]; materials?: any[]; checks?: any[] }) {
  return request<{ data: any }>('/batch-records/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBatchRecordTemplate(id: string, data: { templateName?: string; steps?: any[]; materials?: any[]; checks?: any[]; status?: string }) {
  return request<{ data: any }>(`/batch-records/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBatchRecordTemplate(id: string) {
  return request<{ success: boolean }>(`/batch-records/templates/${id}`, { method: 'DELETE' });
}

// Records
export async function getBatchRecords(params?: { batchId?: string; templateId?: string; status?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.batchId) query.set('batchId', params.batchId);
  if (params?.templateId) query.set('templateId', params.templateId);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/batch-records/records?${query}`);
}

export async function getBatchRecord(id: string) {
  return request<{ data: any }>(`/batch-records/records/${id}`);
}

export async function createBatchRecord(data: { batchId: string; templateId: string; startedBy?: string }) {
  return request<{ data: any }>('/batch-records/records', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBatchRecord(id: string, data: { status?: string; notes?: string; completedAt?: string }) {
  return request<{ data: any }>(`/batch-records/records/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function completeBatchRecord(id: string, data?: { notes?: string }) {
  return request<{ success: boolean }>(`/batch-records/records/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Steps
export async function getBatchRecordSteps(recordId: string) {
  return request<{ data: any[] }>(`/batch-records/records/${recordId}/steps`);
}

export async function updateBatchRecordStep(recordId: string, stepId: string, data: { status?: string; actualValue?: string; completedBy?: string; completedAt?: string; notes?: string }) {
  return request<{ data: any }>(`/batch-records/steps/${stepId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function completeBatchRecordStep(recordId: string, stepId: string, data: { actualValue?: string; notes?: string }) {
  return request<{ success: boolean }>(`/batch-records/${recordId}/steps/${stepId}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Materials
export async function getBatchRecordMaterials(recordId: string) {
  return request<{ data: any[] }>(`/batch-records/records/${recordId}/materials`);
}

export async function updateBatchRecordMaterial(recordId: string, materialId: string, data: { actualQuantity?: string; lotNumber?: string; addedBy?: string; addedAt?: string; notes?: string }) {
  return request<{ data: any }>(`/batch-records/${recordId}/materials/${materialId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Deviations
export async function getBatchRecordDeviations(recordId: string) {
  return request<{ data: any[] }>(`/batch-records/records/${recordId}/deviations`);
}

export async function createBatchRecordDeviation(recordId: string, data: { stepId?: string; deviationType: string; description: string; severity?: string; correctiveAction?: string }) {
  return request<{ data: any }>(`/batch-records/records/${recordId}/deviations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBatchRecordDeviation(recordId: string, deviationId: string, data: { status?: string; resolution?: string; resolvedBy?: string; resolvedAt?: string }) {
  return request<{ data: any }>(`/batch-records/${recordId}/deviations/${deviationId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Checks
export async function getBatchRecordChecks(recordId: string) {
  return request<{ data: any[] }>(`/batch-records/records/${recordId}/checks`);
}

export async function updateBatchRecordCheck(recordId: string, checkId: string, data: { result?: string; checkedBy?: string; checkedAt?: string; notes?: string }) {
  return request<{ data: any }>(`/batch-records/${recordId}/checks/${checkId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Environmental Data
export async function getBatchRecordEnvironmentalData(recordId: string) {
  return request<{ data: any[] }>(`/batch-records/records/${recordId}/environmental`);
}

export async function addBatchRecordEnvironmentalData(recordId: string, data: { temperature?: number; humidity?: number; pressure?: number; recordedAt?: string; notes?: string }) {
  return request<{ data: any }>(`/batch-records/records/${recordId}/environmental`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ==========================================
// CRM (Sales)
// ==========================================

// Segments
export async function getCrmSegments(params?: { status?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/crm/segments?${query}`);
}

export async function getCrmSegment(id: string) {
  return request<{ data: any }>(`/crm/segments/${id}`);
}

export async function createCrmSegment(data: { name: string; description?: string; criteria?: any; isActive?: boolean }) {
  return request<{ data: any }>('/crm/segments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCrmSegment(id: string, data: { name?: string; description?: string; criteria?: any; isActive?: boolean }) {
  return request<{ data: any }>(`/crm/segments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addCrmSegmentMember(segmentId: string, data: { customerId: string }) {
  return request<{ success: boolean }>(`/crm/segments/${segmentId}/members`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeCrmSegmentMember(segmentId: string, customerId: string) {
  return request<{ success: boolean }>(`/crm/segments/${segmentId}/members/${customerId}`, { method: 'DELETE' });
}

// Meetings
export async function getCrmMeetings(params?: { customerId?: string; userId?: string; status?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.userId) query.set('userId', params.userId);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/crm/meetings?${query}`);
}

export async function getCrmMeeting(id: string) {
  return request<{ data: any }>(`/crm/meetings/${id}`);
}

export async function createCrmMeeting(data: { customerId: string; title: string; description?: string; scheduledAt: string; duration?: number; location?: string; attendees?: string[] }) {
  return request<{ data: any }>('/crm/meetings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCrmMeeting(id: string, data: { title?: string; description?: string; scheduledAt?: string; duration?: number; location?: string; status?: string }) {
  return request<{ data: any }>(`/crm/meetings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function completeCrmMeeting(id: string, data?: { notes?: string; outcome?: string }) {
  return request<{ success: boolean }>(`/crm/meetings/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Tasks
export async function getCrmTasks(params?: { customerId?: string; assignedTo?: string; status?: string; priority?: string; dueDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.assignedTo) query.set('assignedTo', params.assignedTo);
  if (params?.status) query.set('status', params.status);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.dueDate) query.set('dueDate', params.dueDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/crm/tasks?${query}`);
}

export async function getCrmTask(id: string) {
  return request<{ data: any }>(`/crm/tasks/${id}`);
}

export async function createCrmTask(data: { customerId?: string; title: string; description?: string; assignedTo?: string; dueDate?: string; priority?: string }) {
  return request<{ data: any }>('/crm/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCrmTask(id: string, data: { title?: string; description?: string; assignedTo?: string; dueDate?: string; priority?: string; status?: string }) {
  return request<{ data: any }>(`/crm/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function completeCrmTask(id: string, data?: { notes?: string }) {
  return request<{ success: boolean }>(`/crm/tasks/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Surveys
export async function getCrmSurveys(params?: { status?: string; type?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/crm/surveys?${query}`);
}

export async function getCrmSurvey(id: string) {
  return request<{ data: any }>(`/crm/surveys/${id}`);
}

export async function createCrmSurvey(data: { title: string; description?: string; questions: any[]; isActive?: boolean }) {
  return request<{ data: any }>('/crm/surveys', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitCrmSurveyResponse(surveyId: string, data: { customerId?: string; responses: any[] }) {
  return request<{ success: boolean }>(`/crm/surveys/${surveyId}/responses`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCrmDashboardStats() {
  return request<{ data: any }>('/crm/dashboard');
}

export async function getCrmPipelineSummary() {
  return request<{ data: any }>('/crm/opportunities/pipeline');
}

// ==========================================
// INVENTORY (Warehouse Management)
// ==========================================

// Zones
export async function getWarehouseZones(warehouseId: string) {
  return request<{ data: any[] }>(`/warehouses/${warehouseId}/zones`);
}

export async function createWarehouseZone(warehouseId: string, data: { zoneName: string; zoneType?: string; description?: string }) {
  return request<{ data: any }>(`/warehouses/${warehouseId}/zones`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWarehouseZone(zoneId: string, data: { zoneName?: string; zoneType?: string; description?: string; isActive?: boolean }) {
  return request<{ data: any }>(`/warehouses/zones/${zoneId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Bins
export async function getWarehouseBins(warehouseId: string, params?: { zoneId?: string; status?: string }) {
  const query = new URLSearchParams();
  if (params?.zoneId) query.set('zoneId', params.zoneId);
  if (params?.status) query.set('status', params.status);
  return request<{ data: any[] }>(`/warehouses/${warehouseId}/bins?${query}`);
}

export async function createWarehouseBin(warehouseId: string, data: { binCode: string; zoneId?: string; binType?: string; capacity?: number }) {
  return request<{ data: any }>(`/warehouses/${warehouseId}/bins`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWarehouseBin(binId: string, data: { binCode?: string; binType?: string; capacity?: number; isActive?: boolean }) {
  return request<{ data: any }>(`/warehouses/bins/${binId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function blockWarehouseBin(binId: string, data?: { reason?: string }) {
  return request<{ success: boolean }>(`/warehouses/bins/${binId}/block`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function getBinInventory(binId: string) {
  return request<{ data: any[] }>(`/warehouses/bins/${binId}/inventory`);
}

// Pick Lists
export async function getPickLists(params?: { warehouseId?: string; status?: string; assignedTo?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.status) query.set('status', params.status);
  if (params?.assignedTo) query.set('assignedTo', params.assignedTo);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/pick-lists?${query}`);
}

export async function getPickList(id: string) {
  return request<{ data: any }>(`/pick-lists/${id}`);
}

export async function createPickList(data: { warehouseId: string; orderId?: string; items: any[] }) {
  return request<{ data: any }>('/pick-lists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function assignPickList(id: string, data: { assignedTo: string }) {
  return request<{ success: boolean }>(`/pick-lists/${id}/assign`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function pickPickListLine(lineId: string, data: { quantityPicked: number; binId?: string; lotNumber?: string }) {
  return request<{ success: boolean }>(`/pick-lists/lines/${lineId}/pick`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Cycle Counts (Inventory)
export async function getInventoryCycleCounts(params?: { warehouseId?: string; status?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/cycle-counts?${query}`);
}

export async function getInventoryCycleCount(id: string) {
  return request<{ data: any }>(`/cycle-counts/${id}`);
}

export async function createInventoryCycleCount(data: { warehouseId: string; zoneId?: string; countType?: string; items?: any[] }) {
  return request<{ data: any }>('/cycle-counts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function startInventoryCycleCount(id: string) {
  return request<{ success: boolean }>(`/cycle-counts/${id}/start`, { method: 'POST' });
}

export async function countInventoryCycleCountLine(lineId: string, data: { countedQuantity: number; notes?: string }) {
  return request<{ success: boolean }>(`/cycle-counts/lines/${lineId}/count`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function completeInventoryCycleCount(id: string, data?: { notes?: string; approveVariances?: boolean }) {
  return request<{ success: boolean }>(`/cycle-counts/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Stock Movements
export async function getStockMovements(params?: { warehouseId?: string; materialId?: string; movementType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.materialId) query.set('materialId', params.materialId);
  if (params?.movementType) query.set('movementType', params.movementType);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return request<{ data: any[]; total: number }>(`/movements?${query}`);
}

export async function createStockMovement(data: { warehouseId: string; materialId: string; movementType: string; quantity: number; fromBinId?: string; toBinId?: string; lotNumber?: string; reason?: string }) {
  return request<{ data: any }>('/movements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Email Inbox
// ============================================

export async function getEmails(params?: { limit?: number; offset?: number; folder?: 'inbox' | 'archive' | 'spam' | 'trash'; isRead?: boolean; search?: string }) {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.folder) query.set('folder', params.folder);
  if (params?.isRead !== undefined) query.set('isRead', params.isRead.toString());
  if (params?.search) query.set('search', params.search);
  return request<{
    data: EmailMessage[];
    pagination: { limit: number; offset: number; total: number };
  }>(`/emails?${query}`);
}

export async function getEmailStats() {
  return request<{ data: { inbox: number; unread: number; starred: number; spam: number } }>('/emails/stats');
}

export async function getEmail(id: string) {
  return request<{ data: EmailMessageWithBody }>(`/emails/${id}`);
}

export async function updateEmail(id: string, data: { isRead?: boolean; isStarred?: boolean; isArchived?: boolean; isSpam?: boolean; folder?: string }) {
  return request<{ data: { id: string } }>(`/emails/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function markEmailAsRead(id: string) {
  return request<{ data: { id: string } }>(`/emails/${id}/read`, {
    method: 'PUT',
  });
}

export async function deleteEmail(id: string, permanent?: boolean) {
  const query = permanent ? '?permanent=true' : '';
  return request<{ success: boolean }>(`/emails/${id}${query}`, {
    method: 'DELETE',
  });
}

export function getEmailAttachmentUrl(emailId: string, attachmentId: string) {
  return `${API_BASE}/emails/${emailId}/attachments/${attachmentId}`;
}

// Email types
export interface EmailMessage {
  id: string;
  messageId: string | null;
  from: string;
  fromName: string | null;
  to: string;
  subject: string | null;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  receivedAt: string;
  folder: string;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
}

export interface EmailMessageWithBody extends EmailMessage {
  replyTo: string | null;
  textBody: string | null;
  htmlBody: string | null;
  rawHeaders: Record<string, string>;
  isArchived: boolean;
  isSpam: boolean;
  size: number | null;
  createdAt: string;
  updatedAt: string;
  attachments: EmailAttachment[];
}

// Contexa types
export interface ContextaJob {
  id: string;
  productionOrderId: string;
  system: 'COLIBRI' | 'CP_START' | 'TEX';
  status: 'pending' | 'sent' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  targetQuantity: string;
  actualQuantity: string | null;
  unit: string;
  fileName: string | null;
  errorMessage: string | null;
  sentAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContextaJobWithContent extends ContextaJob {
  order: {
    id: string;
    orderNumber: string;
  } | null;
  formula: {
    id: string;
    code: string;
    name: string;
  } | null;
  impContent: string | null;
}
